import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import * as models from '../../../../models'
import type { Record } from '../../../record'
import { Value } from '../../../value'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateObject } from './create-object'
import { If } from './if'
import { NotEquals } from './not-equals'

/**
 * Retrieves the value of an attribute on the query entity
 *
 * @param partialId Partial ID of the desired attribute
 * @example
 * { '@.': '!name' }
 * // ->
 * 'Room 100'
 *
 * { '@.': '!ref' }
 * // ->
 * {
 *   'entityId': '?some-referenced-entity-id',
 *   'recordId': '?some-referenced-record-id',
 * }
 *
 * { '@.': ['!ref', { ref: { entityIdFormat: models.Id.Format.Full } }] }
 * // ->
 * {
 *   'entity': 'ent:!fa_func:!subcat',
 *   'record': 'xyz',
 * }
 *
 * { '@.': ['!ref', { ref: { entityIdFormat: models.Id.Format.Full, format: data.Value.Reference.Format.Dimension } }] }
 * // ->
 * {
 *   'ent:!fa_func:!cat': '?abc',
 *   'ent:!fa_func:!subcat': '?xyz',
 * }
 */
export interface GetAttribute<out T = any> {
  /** {@link GetAttribute} */
  readonly [Key.GetAttribute]:
    | PartialId
    | [attributePartialId: PartialId, options: GetAttribute.Options]
}
export namespace GetAttribute {
  export const isGetAttribute: TypeChecker<GetAttribute> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<GetAttribute>({
        [Key.GetAttribute]: TypeChecker.Factory.union(
          PartialId.isPartialId,
          TypeChecker.Factory.tuple(PartialId.isPartialId, Options.isOptions),
        ),
      }),
    ).usingAlias('Fn.GetAttribute')
  export const assertIsGetAttribute: TypeAsserter<GetAttribute> =
    TypeAsserter.Factory.from(isGetAttribute)

  export interface Options {
    ref?: Options.ForRef
  }
  export namespace Options {
    export const isOptions: TypeChecker<Options> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.object<Options>({
          ref: TypeChecker.Factory.optional(ForRef.isForRef),
        }),
    )

    export interface ForRef {
      /**
       * Can be used to specify a specific format in which the data should be
       * returned. Particularly useful for reference attributes
       *
       * @default entity !== undefined ? Value.Reference.Format.RecordId : Value.Reference.Format.Entity
       * @see {@link Value.Reference.Format}
       */
      format?: `${Value.Reference.Format}`
      /**
       * When using a {@link format} that includes entity IDs, this indicates
       * whether the result should contain full entity IDs or partial entity IDs
       *
       * @default Id.Format.Partial
       */
      entityIdFormat?: `${models.Id.Format}`
      /**
       * When using a {@link format} that targets a specific entity, this
       * determines which entity is targeted. Please use with caution and see
       * notes below
       *
       * __NOTE 1:__ there is no guarantee that this entity is the one being
       * referenced for a given record. For example, if the entity specified
       * here is `!bldg` and a given record references a `!room`, the `!bldg`
       * associated with that `!room` is what will be returned. Also, if the
       * record references a `!camp`, then the result will be undefined/null.
       *
       * __NOTE 2:__ this cannot be used with the {@link Value.Reference.Format.Dimension}
       * {@link format}
       *
       * @default each record uses the entity that is being referenced by that
       * record
       */
      entity?: models.Entity.Id | PartialId
    }
    export namespace ForRef {
      export const isForRef: TypeChecker<ForRef> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.object<ForRef>({
            entity: TypeChecker.Factory.optional(
              TypeChecker.Factory.union(
                models.Entity.Id.isId,
                PartialId.isPartialId,
              ),
            ),
            entityIdFormat: TypeChecker.Factory.optional(
              models.Id.Format.isFormat,
            ),
            format: TypeChecker.Factory.optional(
              Value.Reference.Format.isFormat,
            ),
          }),
      )

      export type WithDefaults = Required.Semi<
        ForRef,
        'format' | 'entityIdFormat'
      >
      export namespace WithDefaults {
        export function from({
          entity,
          entityIdFormat = models.Id.Format.Partial,
          format = entity !== undefined
            ? Value.Reference.Format.RecordId
            : Value.Reference.Format.Entity,
        }: ForRef = {}): WithDefaults {
          return {
            entity,
            entityIdFormat,
            format,
          } satisfies NoMissing<WithDefaults>
        }
      }
    }
  }

  /**
   * @see {@link Node}
   */
  export class Node<
    T extends Object.Constraint | string = Object.Constraint | string,
  > extends fnNode.Node<
    T,
    Key.GetAttribute,
    {
      attributePartialId: PartialId
      /**
       * `undefined` for non-reference attributes
       */
      refEntityPartialId?: PartialId
    }
  > {
    public readonly type: common.value.types.Type

    constructor(arg: Readonly<Node<T>['arg']>, type: common.value.types.Type) {
      super(Key.GetAttribute, arg)

      this.type = type
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<GetAttribute> = {
    createNode: (fn, context) => {
      // TODO: more useful error message
      TypeAsserter.assertIsNotNullish(context.modelId)
      // First we parse the arg
      const arg = fn[Key.GetAttribute]
      let attributePartialId: PartialId
      let options: Options | undefined
      if (TypeChecker.isReadonlyArray(arg)) {
        attributePartialId = arg[0]
        options = arg[1]
      } else {
        attributePartialId = arg
      }

      // Then we get all of the relevant info about the attribute
      const attributeFullId = models.Attribute.Id.from({
        context: models.Id.Separator.swap(
          context.modelId,
          models.Id.Separator.BetweenSections,
          models.Id.Separator.WithinSection,
        ),
        partialId: attributePartialId,
      })
      const attribute = context.schema.getAttribute(attributeFullId)

      // Next we check if it is a reference
      if (!Value.Type.Reference.isReference(attribute.type)) {
        // Non-references are easy:
        return new Node(
          { attributePartialId },
          context.schema.getType(attribute.type),
        )
      } else {
        // For references, we first need to pull out a number of options that
        // determine what we actually return
        const type = common.value.types.Intrinsic.Text
        const {
          entity: entityIdOrPartialId,
          entityIdFormat,
          format,
          // NOTE: nullish coalescing w/ `undefined` prevents bugs when
          // options.ref is `null` (which shouldn't happen, but can when
          // creating queries with a query; e.g., tables)
        } = Options.ForRef.WithDefaults.from(options?.ref ?? undefined)
        const { dimensionId } = attribute.type
        const useFullEntityId = entityIdFormat === models.Id.Format.Full

        // For dimension format, we can ignore any specified entity and we just
        // create an object with all relevant reference IDs
        if (format === Value.Reference.Format.Dimension) {
          const obj: Mutable<CreateObject.Node['arg']> = {}
          for (const entity of context.schema.getEntitiesInDimension(
            dimensionId,
          )) {
            const refEntityPartialId = models.Id.Parts.from(entity.id).partialId
            obj[useFullEntityId ? entity.id : refEntityPartialId] = new Node(
              {
                attributePartialId,
                refEntityPartialId: refEntityPartialId,
              },
              type,
            )
          }
          return new CreateObject.Node(obj)
        }

        // For all other formats, the result is for a particular entity

        const dimensionPartialId = models.Id.Parts.from(dimensionId).partialId

        /**
         * Solely used to help TypeScript understand that {@link format} is not
         * going to change values before {@link getResultNodeForEntity} is
         * called
         *
         * Basically, this allows {@link TypeAsserter.assertIsNever} to work in
         * {@link getResultNodeForEntity}
         */
        const remainingFormat = format
        /** Returns the result in the requested format for the given entity */
        function getResultNodeForEntity({
          entityId,
          entityPartialId,
        }: Readonly<{
          entityId: models.Entity.Id
          entityPartialId: PartialId
        }>): expressionNode.Node {
          const entityIdNode = new expressionNode.ForValue(
            useFullEntityId ? entityId : entityPartialId,
            type,
          )
          const recordIdNode = new Node<Record.Id>(
            {
              attributePartialId,
              refEntityPartialId: entityPartialId,
            },
            type,
          )
          switch (remainingFormat) {
            case 'ent':
              return new CreateObject.Node<
                Value.Reference<
                  Extract<Value.Reference.Format, typeof remainingFormat>
                >
              >({
                entityId: entityIdNode,
                recordId: recordIdNode,
              })
            case 'ent-id':
              return entityIdNode
            case 'record-id':
              return recordIdNode
            default:
              throw TypeAsserter.assertIsNever(remainingFormat)
          }
        }

        if (entityIdOrPartialId) {
          // If there is a specified entity, we simply return the results for
          // that entity
          let entityId: models.Entity.Id
          let entityPartialId: PartialId
          if (models.Entity.Id.isId(entityIdOrPartialId)) {
            entityId = entityIdOrPartialId
            entityPartialId = models.Id.Parts.from(entityId).partialId
          } else {
            entityId = models.Entity.Id.from({
              context: dimensionPartialId,
              partialId: entityIdOrPartialId,
            })
            entityPartialId = entityIdOrPartialId
          }
          return getResultNodeForEntity({ entityId, entityPartialId })
        } else {
          // If there is not a specified entity ID, then we need to determine
          // the referenced entity ID for each record
          const cases = context.schema
            .getEntitiesInDimension(dimensionId)
            // We start with the leaf entity
            .reverse()
            .map(entity => {
              const entityPartialId = models.Id.Parts.from(entity.id).partialId
              // The first entity that we encounter where the reference is not
              // undefined/null, that is the referenced entity
              return {
                then: getResultNodeForEntity({
                  entityId: entity.id,
                  entityPartialId,
                }),
                when: new NotEquals.Node([
                  // Reference ID for this entity
                  new Node(
                    {
                      attributePartialId,
                      refEntityPartialId: entityPartialId,
                    },
                    type,
                  ),
                  // != undefined
                  new expressionNode.ForValue(undefined),
                ]),
              }
            })
          switch (cases.length) {
            case 0:
              return new expressionNode.ForValue(undefined)
            case 1:
              return cases[0].then
            default:
              return new If.Node({
                cases,
                else: new expressionNode.ForValue(undefined),
              })
          }
        }
      }
    },
    expressionsInArgs: fn => [],
    typeChecker: isGetAttribute,
  }
}
